// pages/Traders/EditTrader.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { 
  ArrowLeft, Save, X, User, Building2, Phone, Mail, 
  MapPin, FileText, Upload, Loader2, CheckCircle, 
  AlertCircle, Briefcase, Globe, Home, Lock
} from "lucide-react";

function EditTrader() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    tin: "",
    name: "",
    business_name: "",
    phone: "",
    email: "",
    city: "",
    subcity: "",
    woreda: "",
    address: "",
    latitude: "",
    longitude: "",
    status: "pending",
    business_license: "",
    national_id: "",
  });
  
  const [existingFiles, setExistingFiles] = useState({
    license_photo: null,
    national_id_photo: null,
  });
  
  const [newFiles, setNewFiles] = useState({
    license_photo: null,
    national_id_photo: null,
  });
  
  const [previews, setPreviews] = useState({
    license_photo: "",
    national_id_photo: "",
  });

  // API instance
  const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000',
    headers: { 'Accept': 'application/json' }
  });

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Fetch trader data
  const fetchTrader = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/traders/${id}`);
      const trader = response.data.data || response.data;
      
      setFormData({
        tin: trader.tin || "",
        name: trader.name || "",
        business_name: trader.business_name || "",
        phone: trader.phone || "",
        email: trader.email || "",
        city: trader.city || "",
        subcity: trader.subcity || "",
        woreda: trader.woreda || "",
        address: trader.address || trader.location || "",
        latitude: trader.latitude || "",
        longitude: trader.longitude || "",
        status: trader.status || "pending",
        business_license: trader.business_license || "",
        national_id: trader.national_id || "",
      });
      
      setExistingFiles({
        license_photo: trader.license_photo || null,
        national_id_photo: trader.national_id_photo || null,
      });
      
    } catch (error) {
      console.error("Fetch error:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load trader data',
        timer: 3000,
        showConfirmButton: false
      });
      navigate('/admin/traders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrader();
  }, [id]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle file upload
  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire('Error', 'File size must be less than 5MB', 'error');
        return;
      }
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        Swal.fire('Error', 'Only JPG, PNG, JPEG, and PDF files are allowed', 'error');
        return;
      }
      
      setNewFiles(prev => ({ ...prev, [field]: file }));
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const url = URL.createObjectURL(file);
        setPreviews(prev => ({ ...prev, [field]: url }));
      } else {
        setPreviews(prev => ({ ...prev, [field]: null }));
      }
    }
  };

  // Remove file
  const removeFile = (field) => {
    setNewFiles(prev => ({ ...prev, [field]: null }));
    if (previews[field]) {
      URL.revokeObjectURL(previews[field]);
    }
    setPreviews(prev => ({ ...prev, [field]: "" }));
  };

  // Remove existing file
  const removeExistingFile = async (field) => {
    const result = await Swal.fire({
      title: 'Remove File?',
      text: `Are you sure you want to remove this file?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      confirmButtonText: 'Yes, remove',
      cancelButtonText: 'Cancel'
    });
    
    if (result.isConfirmed) {
      setExistingFiles(prev => ({ ...prev, [field]: null }));
    }
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setSubmitting(true);
    
    try {
      const submitData = new FormData();
      
      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (formData[key] !== undefined && formData[key] !== null && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });
      
      // Add existing files status
      submitData.append('remove_license', existingFiles.license_photo === null ? '1' : '0');
      submitData.append('remove_national_id', existingFiles.national_id_photo === null ? '1' : '0');
      
      // Add new files
      if (newFiles.license_photo) {
        submitData.append('license_photo', newFiles.license_photo);
      }
      if (newFiles.national_id_photo) {
        submitData.append('national_id_photo', newFiles.national_id_photo);
      }
      
      // Add method spoofing for PUT
      submitData.append('_method', 'PUT');
      
      const response = await api.post(`/api/traders/${id}`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success || response.status === 200) {
        Swal.fire({
          icon: 'success',
          title: 'Updated!',
          text: 'Trader information has been updated successfully.',
          timer: 2000,
          showConfirmButton: false
        });
        navigate('/admin/traders');
      }
      
    } catch (error) {
      console.error("Update error:", error);
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: error.response?.data?.message || 'Failed to update trader information',
        confirmButtonText: 'Try Again'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Input field component
  const InputField = ({ label, name, type = "text", required = false, icon: Icon, placeholder }) => (
    <div className="space-y-1.5">
      <label className="block text-sm font-semibold text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative group">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="h-5 w-5 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
        </div>
        <input
          type={type}
          name={name}
          value={formData[name]}
          onChange={handleChange}
          required={required}
          className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 
                   rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 
                   focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white"
          placeholder={placeholder}
        />
      </div>
    </div>
  );

  const SelectField = ({ label, name, options, required = false, icon: Icon }) => (
    <div className="space-y-1.5">
      <label className="block text-sm font-semibold text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="h-5 w-5 text-gray-400" />
        </div>
        <select
          name={name}
          value={formData[name]}
          onChange={handleChange}
          required={required}
          className="block w-full pl-10 pr-10 py-2.5 border border-gray-300 
                   rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 
                   focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white
                   appearance-none cursor-pointer"
        >
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const TextAreaField = ({ label, name, rows = 3, icon: Icon, placeholder }) => (
    <div className="space-y-1.5">
      <label className="block text-sm font-semibold text-gray-700">{label}</label>
      <div className="relative">
        <div className="absolute top-3 left-0 pl-3 pointer-events-none">
          <Icon className="h-5 w-5 text-gray-400" />
        </div>
        <textarea
          name={name}
          value={formData[name]}
          onChange={handleChange}
          rows={rows}
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 
                   rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 
                   focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white"
          placeholder={placeholder}
        />
      </div>
    </div>
  );

  const FileField = ({ label, field, existingFile }) => (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-gray-700">{label}</label>
      
      {/* Existing file display */}
      {existingFile && !newFiles[field] && (
        <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-green-600" />
            <span className="text-sm text-green-700">Current file uploaded</span>
          </div>
          <button
            type="button"
            onClick={() => removeExistingFile(field)}
            className="text-red-600 hover:text-red-700 text-sm font-medium"
          >
            Remove
          </button>
        </div>
      )}
      
      {/* New file upload */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-indigo-500 transition-colors">
        <input
          type="file"
          id={field}
          accept="image/*,application/pdf"
          onChange={(e) => handleFileChange(e, field)}
          className="hidden"
        />
        <label htmlFor={field} className="cursor-pointer flex flex-col items-center">
          <Upload className="h-8 w-8 text-gray-400 mb-2" />
          <span className="text-sm text-gray-600">
            {newFiles[field] ? newFiles[field].name : 'Click to upload new file'}
          </span>
          <span className="text-xs text-gray-400 mt-1">JPG, PNG, PDF (Max 5MB)</span>
        </label>
      </div>
      
      {/* Preview for new image */}
      {previews[field] && (
        <div className="relative inline-block mt-2">
          <img src={previews[field]} alt="Preview" className="max-h-32 rounded-lg border" />
          <button
            type="button"
            onClick={() => removeFile(field)}
            className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
          >
            <X size={14} />
          </button>
        </div>
      )}
      
      {/* Show remove option for new file */}
      {newFiles[field] && !previews[field] && (
        <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
          <span className="text-sm text-gray-600">{newFiles[field].name}</span>
          <button
            type="button"
            onClick={() => removeFile(field)}
            className="text-red-600 hover:text-red-700"
          >
            <X size={16} />
          </button>
        </div>
      )}
    </div>
  );

  const statusOptions = [
    { value: 'pending', label: 'Pending Approval' },
    { value: 'approved', label: 'Approved' },
    { value: 'active', label: 'Active' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'suspended', label: 'Suspended' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="relative inline-block">
            <div className="w-16 h-16 border-4 border-indigo-200 rounded-full"></div>
            <div className="absolute top-0 left-0 w-16 h-16 border-4 border-indigo-700 rounded-full animate-spin border-t-transparent"></div>
          </div>
          <p className="mt-4 text-gray-600 font-medium">Loading trader data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 p-4 md:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link to="/admin/traders" className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 mb-4">
            <ArrowLeft size={20} />
            Back to Traders
          </Link>
          
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-8">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-white mb-2">Edit Trader</h1>
                  <p className="text-indigo-100">Update trader information and documents</p>
                </div>
                <div className="hidden md:block">
                  <Building2 className="h-16 w-16 text-white opacity-20" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl overflow-hidden p-6 md:p-8">
          <div className="space-y-8">
            {/* Basic Information */}
            <div>
              <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                <User className="h-5 w-5 text-indigo-600" />
                <h2 className="text-lg font-bold text-gray-800">Basic Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <InputField 
                  icon={FileText} 
                  label="TIN Number" 
                  name="tin" 
                  placeholder="Enter TIN number"
                  required
                />
                <InputField 
                  icon={User} 
                  label="Full Name" 
                  name="name" 
                  placeholder="Enter full name"
                  required
                />
                <InputField 
                  icon={Building2} 
                  label="Business Name" 
                  name="business_name" 
                  placeholder="Enter business name"
                  required
                />
                <InputField 
                  icon={Phone} 
                  label="Phone Number" 
                  name="phone" 
                  type="tel"
                  placeholder="Enter phone number"
                  required
                />
                <InputField 
                  icon={Mail} 
                  label="Email Address" 
                  name="email" 
                  type="email"
                  placeholder="Enter email address"
                  required
                />
                <SelectField 
                  icon={AlertCircle} 
                  label="Status" 
                  name="status" 
                  options={statusOptions}
                />
              </div>
            </div>

            {/* Address Information */}
            <div>
              <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                <MapPin className="h-5 w-5 text-indigo-600" />
                <h2 className="text-lg font-bold text-gray-800">Address Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <InputField icon={Globe} label="City" name="city" placeholder="Enter city" />
                <InputField icon={MapPin} label="Subcity" name="subcity" placeholder="Enter subcity" />
                <InputField icon={MapPin} label="Woreda" name="woreda" placeholder="Enter woreda" />
              </div>
              <div className="mt-5">
                <TextAreaField 
                  icon={Home} 
                  label="Full Address" 
                  name="address" 
                  rows={2}
                  placeholder="Enter complete address"
                />
              </div>
            </div>

            {/* GPS Location */}
            <div>
              <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                <MapPin className="h-5 w-5 text-indigo-600" />
                <h2 className="text-lg font-bold text-gray-800">GPS Location</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <InputField icon={MapPin} label="Latitude" name="latitude" type="number" step="any" placeholder="Enter latitude" />
                <InputField icon={MapPin} label="Longitude" name="longitude" type="number" step="any" placeholder="Enter longitude" />
              </div>
              <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                <Lock size={12} />
                GPS coordinates help verify the trader's business location
              </p>
            </div>

            {/* Documents */}
            <div>
              <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                <FileText className="h-5 w-5 text-indigo-600" />
                <h2 className="text-lg font-bold text-gray-800">Supporting Documents</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FileField 
                  label="Business License" 
                  field="license_photo" 
                  existingFile={existingFiles.license_photo}
                />
                <FileField 
                  label="National ID" 
                  field="national_id_photo" 
                  existingFile={existingFiles.national_id_photo}
                />
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="pt-6 border-t border-gray-200 flex flex-col sm:flex-row gap-3 justify-end">
              <Link
                to="/admin/traders"
                className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition text-center"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={submitting}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Saving Changes...
                  </>
                ) : (
                  <>
                    <Save className="h-5 w-5" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditTrader;